import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { shipmentService } from '../services/firebase';
import { Shipment } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import LoadError from '../components/LoadError';

const STATUSES = ['all', 'pending', 'in_transit', 'delivered', 'cancelled'] as const;
type StatusFilter = (typeof STATUSES)[number];

export default function Shipments() {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await shipmentService.getAllShipments();
      setShipments(data);
    } catch (err) {
      console.error("Failed to fetch shipments:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return shipments.filter((s) => {
      if (status !== 'all' && s.status !== status) return false;
      if (!q) return true;
      return [s.trackingNumber, s.customer?.name, s.origin?.city, s.destination?.city]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [shipments, query, status]);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Shipments</h1>
        <Link to="/shipments/new" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
          Add Shipment
        </Link>
      </div>

      {!loading && !error && shipments.length > 0 && (
        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by tracking number, customer, or city…"
              aria-label="Search shipments"
              className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
            aria-label="Filter by status"
            className="rounded-lg border border-gray-300 py-2.5 pl-3 pr-9 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All statuses' : s.replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>
      )}

      {!loading && !error && shipments.length > 0 && (
        <p className="mb-3 text-sm text-gray-500">
          Showing {visible.length} of {shipments.length}
        </p>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <LoadError subject="shipments" onRetry={fetchShipments} />
      ) : shipments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
           <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No shipments</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new shipment.</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {visible.map((shipment) => (
              <li key={shipment.id}>
                <Link to={`/shipments/${shipment.id}`} className="block hover:bg-gray-50">
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-blue-600 truncate">{shipment.trackingNumber}</p>
                      <div className="ml-2 flex-shrink-0 flex">
                        <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          shipment.status === 'delivered' ? 'bg-green-100 text-green-800' :
                          shipment.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                          shipment.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {String(shipment.status || 'pending').replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        <p className="flex items-center text-sm text-gray-500">
                         {shipment.customer?.name || <span className="italic text-gray-400">No customer</span>}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                         <p className="mr-6">
                            From: <span className="font-medium">{shipment.origin?.city || 'N/A'}</span>
                          </p>
                          <p>
                            To: <span className="font-medium">{shipment.destination?.city || 'N/A'}</span>
                          </p>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
} 